import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { BaseGrid, BaseGridItem, } from '@awaze/design-system.components.base-grid';
import { Datepicker } from '@awaze/design-system.components.datepicker';
import { Stack } from '@awaze/design-system.components.stack';
import { breakpoints, getShadowStyle, pxToRems, } from '@awaze/design-system.utils.react';
import { addDays, differenceInMonths, differenceInDays, formatISO, parse, } from 'date-fns';
import styled from 'styled-components';
import { StartDateInput } from './start-date-input';
import { DurationInput } from './duration-input';
import { FlexibilityInput } from './flexibility-input';
const StyledDurationFlexibilitySticky = styled.div `
  box-shadow: ${({ theme }) => getShadowStyle({ theme, depth: 4 })};
  padding: ${pxToRems(16)} ${pxToRems(16)} ${pxToRems(8)};
  position: sticky;
  top: 0;
  left: 0;
  background-color: ${({ theme }) => theme.ColorWhiteOpacity100};
  z-index: 1;

  @media screen and (min-width: ${breakpoints[0]}px) {
    box-shadow: none;
    padding: 0;
    position: initial;
    z-index: initial;
  }
`;
const StyledScrollableArea = styled.div `
  @media screen and (max-width: ${breakpoints[0]}px) {
    overflow: scroll;
  }
`;
const Spacer = styled.div `
  margin-bottom: ${pxToRems(24)};
`;
const DurationAndFlexibility = ({ showDurationInput, duration, formHooks, labels, flexibility, }) => (_jsx(_Fragment, { children: showDurationInput ? (_jsxs(_Fragment, { children: [_jsx(BaseGridItem, { columns: 6, children: _jsx(DurationInput, { duration: duration, formHooks: formHooks, labels: labels }) }), _jsx(BaseGridItem, { columns: 6, children: _jsx(FlexibilityInput, { flexibility: flexibility, formHooks: formHooks, labels: labels }) })] })) : (_jsx(BaseGridItem, { columns: 6, children: _jsx(FlexibilityInput, { flexibility: flexibility, formHooks: formHooks, labels: labels }) })) }));
/**
 * A group of form controls for date and duration of a holiday. The `<DurationInput>`, `<FlexibilityInput>` and `<StartDateInput>` can also be used individually if more control over the layout is needed.
 * */
export const SearchDateInputs = ({ dates, duration, flexibility, formHooks, labels, isCalendar = false, horizontal, monthsToShow, showNavigation, calendarTranslations, locale = 'en-EN', showDurationInput = true, maxAmountOfNights, }) => {
    /**
     * Add on 2 extra months to ensure all available dates are shown
     */
    const numberOfMonthsToShow = monthsToShow || differenceInMonths(dates.max, new Date()) + 2;
    const selectedDuration = Number(formHooks.watch('nights'));
    // Create the object with dates and prices
    const setAvailableDatesFrom = (date) => {
        const availabilityDates = {};
        const initialDate = parse(formatISO(date, { representation: 'date' }), 'yyyy-MM-dd', new Date());
        for (let i = 0; i <= maxAmountOfNights; i++) {
            const currentDate = addDays(initialDate, i);
            const formattedDate = formatISO(currentDate, {
                representation: 'date',
            });
            availabilityDates[formattedDate] = { price: null };
        }
        formHooks.setValue('availabilityDates', availabilityDates);
    };
    // only enabled when duration input is not shown
    const onDaySelected = (date) => {
        const startDate = formHooks.getValues('start');
        const nights = formHooks.getValues('nights');
        if (formHooks.getValues('start') && !nights) {
            const selectedDate = parse(startDate, 'yyyy-MM-dd', new Date());
            const diffInDays = differenceInDays(date, selectedDate).toString();
            formHooks.setValue('nights', diffInDays);
            formHooks.setValue('availabilityDates', undefined);
        }
        else if (nights) {
            formHooks.setValue('start', formatISO(date, { representation: 'date' }));
            formHooks.setValue('nights', 0);
            setAvailableDatesFrom(date);
        }
        else {
            formHooks.setValue('start', formatISO(date, { representation: 'date' }));
            setAvailableDatesFrom(date);
        }
    };
    return (_jsx(_Fragment, { children: !isCalendar ? (_jsxs(BaseGrid, { flush: true, children: [_jsxs(BaseGridItem, { columns: 12, children: [_jsx(StartDateInput, { formHooks: formHooks, minDate: dates.min, maxDate: dates.max }), _jsx(Spacer, {})] }), _jsx(DurationAndFlexibility, { showDurationInput: showDurationInput, duration: duration, formHooks: formHooks, labels: labels, flexibility: flexibility })] })) : (_jsxs(Stack, { spacing: 0, fullHeight: true, children: [_jsx(StyledDurationFlexibilitySticky, { children: _jsx(BaseGrid, { flush: true, children: _jsx(DurationAndFlexibility, { showDurationInput: showDurationInput, duration: duration, formHooks: formHooks, labels: labels, flexibility: flexibility }) }) }), _jsx(StyledScrollableArea, { children: _jsx(Datepicker, { formHooks: formHooks, name: "start", calendarProps: {
                            minDate: dates.min,
                            maxDate: dates.max,
                            monthsToShow: numberOfMonthsToShow,
                            duration: selectedDuration,
                            horizontal,
                            showNavigation,
                            translations: calendarTranslations,
                            locale,
                            freeSelectionMode: !showDurationInput,
                            maxAmountOfNights,
                            ...(!showDurationInput && {
                                availability: formHooks.getValues('availabilityDates'),
                            }),
                            ...(!showDurationInput && { onDaySelected }),
                        } }) })] })) }));
};
//# sourceMappingURL=search-date-inputs.js.map